<script>

    import { onDestroy } from 'svelte'
    import Chart from 'chart.js'
    import Moment from 'moment'
    import { toast } from 'bulma-toast'

    import GraphicsService from '../$services/graphics.service'
    import NumeralMethods from '../$methods/numeral.methods'

    import DateYearNav from '../$components/date.year.nav.svelte'
    import Spinner from '../$components/spinner.svelte'

    const styles = {
        Cargos: { border: '#098E37', bg: 'rgba(9, 142, 55, 0.1)' },
        Reembolsos: { border: '#C8645C', bg: 'rgba(200, 100, 92, 0.1)' },
    }

    let dateStart = Moment().subtract(1, 'year').startOf('day').toDate()
    let dateEnd = Moment().endOf('day').toDate()

    let loading = false
    let labels = []
    let datasets = []
    let chart
    let canvas

    $: periodLabel = dateStart && dateEnd
        ? `${Moment(dateStart).format('D MMM YYYY')} – ${Moment(dateEnd).format('D MMM YYYY')}`
        : ''

    $: if (dateStart && dateEnd)
        getChargesMonths()

    $: labels.length && canvas && setChart()

    onDestroy(() => {
        if (chart)
            chart.destroy()
    })

    async function getChargesMonths() {
        if (chart) {
            chart.destroy()
            chart = null
        }

        loading = true
        labels = []
        datasets = []

        const query = {
            dateStart: dateStart.toISOString(),
            dateEnd: dateEnd.toISOString(),
        }

        const [chargesResp, refundsResp] = await Promise.all([
            GraphicsService.getChargesMonths(query),
            GraphicsService.getChargesRefundsMonths(query),
        ])

        loading = false

        if (!chargesResp.success)
            return toast({ message: chargesResp.error.message, type: 'is-danger' })

        if (!refundsResp.success)
            return toast({ message: refundsResp.error.message, type: 'is-danger' })

        labels = chargesResp.data.labels ?? []
        datasets = [
            ...(chargesResp.data.datasets ?? []),
            ...(refundsResp.data.datasets ?? []),
        ]
    }

    function styleDataset(ds) {
        const { border, bg } = styles[ds.label] ?? styles.Cargos

        return {
            ...ds,
            backgroundColor: bg,
            borderColor: border,
            borderWidth: 2,
            fill: true,
            pointBackgroundColor: border,
            pointRadius: 4,
        }
    }

    function setChart() {
        const chartData = {
            labels,
            datasets: datasets.map(styleDataset),
        }

        if (!chart)
            return chart = new Chart(canvas, {
                type: 'line',
                data: chartData,
                options: {
                    maintainAspectRatio: false,
                    legend: { display: true },
                    tooltips: {
                        callbacks: {
                            label: (item, data) => {
                                const label = data.datasets[item.datasetIndex].label || ''
                                return `${label}: ${NumeralMethods.cash(item.yLabel)}`
                            },
                        },
                    },
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: value => NumeralMethods.cash(value),
                            },
                        }],
                    },
                },
            })

        chart.data = chartData
        chart.update()
    }

</script>

<style>
    .graphic-card {
        background: #fff;
        border-radius: 28px;
        box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
        padding: 1.5rem;
        margin-bottom: 1.25rem;
        display: flex;
        flex-direction: column;
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.25rem;
        gap: 1rem;
        box-shadow: none;
    }

    .logo {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: 1px solid #e8e8e8;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1a1a1a;
        font-size: 1.15rem;
        flex-shrink: 0;
    }

    .meta {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        margin-bottom: 0.35rem;
    }

    .meta-name {
        font-weight: 700;
        color: #1a1a1a;
        font-size: 0.95rem;
    }

    .meta-date {
        color: #9e9e9e;
        font-size: 0.85rem;
    }

    .title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1a1a1a;
        line-height: 1.2;
        margin: 0 0 1.25rem;
    }

    .body {
        padding: 16px;
        border-radius: 20px;
    }

    .chart-wrap {
        position: relative;
        height: 320px;
    }

    .empty {
        color: #9e9e9e;
        font-size: 0.85rem;
        margin: 0;
        text-align: center;
        padding: 4rem 0;
    }
</style>

<div class="graphic-card">
    <div class="card-header">
        <div class="logo">
            <i class="fas fa-file-invoice-dollar"></i>
        </div>
        <DateYearNav bind:dateStart bind:dateEnd />
    </div>

    <div class="meta">
        <span class="meta-name">Cargos</span>
        {#if periodLabel}
            <span class="meta-date">{ periodLabel }</span>
        {/if}
    </div>

    <h3 class="title">Cargos y reembolsos por mes</h3>

    <div class="body">
        <Spinner {loading} />
        {#if !loading && labels.length}
            <div class="chart-wrap">
                <canvas bind:this={canvas}></canvas>
            </div>
        {:else if !loading}
            <p class="empty">Sin datos en el periodo seleccionado</p>
        {/if}
    </div>
</div>
